/**
 * Gotcha these exist for: numeric WebEDI cells register with `{ valueAsNumber: true }`, so an empty
 * one reaches form state as `NaN`, not `undefined`. `Number(row[path] ?? 0)` does not defend against
 * it — `NaN` is not nullish, so `?? 0` never fires and every sum downstream turns into `NaN`.
 *
 * They live in `support/` rather than the modal because the shipping-notice summary reads the same
 * NaN-prone field.
 */

/** `undefined` when the cell holds no quantity — `NaN`, `''`, `null`, `undefined`. A real `0` survives. */
export const toFiniteQuantity = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const quantity = Number(value);
  return Number.isFinite(quantity) ? quantity : undefined;
};

export const readQuantity = (value: unknown): number => toFiniteQuantity(value) ?? 0;

/**
 * Whether `ordered_quantity` can stand in for a line's total. When it can, the total survives
 * anywhere the shipped quantities are rewritten — which is what lets the serializer zero an
 * unplaced remainder instead of storing it (see packingListToFlattenData).
 */
export const canRecoverTotalFromOrdered = (orderedQuantity: unknown): boolean =>
  (toFiniteQuantity(orderedQuantity) ?? 0) > 0;

/**
 * How much of a line can be placed: the ordered quantity when there is one, else what has been
 * shipped. Ordered wins because shipped quantity is the *result* of packing (CORE-11647).
 *
 * Both arguments must be GROUP totals, never a single row's. A split line is re-emitted once per
 * placement with `ordered_quantity` kept on the first copy and zeroed on the rest, so picking per
 * row would take ordered from one copy and shipped from the others and sum them.
 */
export const pickPackableQuantity = (orderedQuantity: number | undefined, shippedQuantity: number): number =>
  canRecoverTotalFromOrdered(orderedQuantity) ? (orderedQuantity ?? 0) : shippedQuantity;
